/**
 * ModuleAreaGuard — wrapper ekranu „obszarowego": renderuje children tylko gdy tenant ma
 * DOWOLNY moduł z obszaru `areaCode` (z aktywną/trialową subskrypcją). W przeciwnym razie
 * plansza „nieaktywny" — ta sama co w `ModuleGuard`, z nazwą obszaru zamiast modułu.
 *
 * Powód: pulpit Kadr/HR (`/hr`) nie należy do jednego modułu — decyzja Eugenii 2026-10-05:
 * jest aktywny, gdy firma ma jakikolwiek moduł z obszaru `hr`. Źródło prawdy to samo co widoku
 * „Kadry" w sidebarze: `GET /api/my/installed-modules` (obszary złożone wyłącznie z opłaconych
 * modułów). Backend trzyma ten sam warunek (`hasActiveModuleInArea` → 402 na `/api/hr/metrics`),
 * więc wejście z palca w URL bez modułu HR nie daje działającej strony.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ModuleGuardError, ModuleGuardLoading, ModuleInactiveNotice } from './ModuleGuard';
import { useInstalledModulesMenuQuery } from './Sidebar';

export interface ModuleAreaGuardProps {
  /** Kod obszaru modułów (`module_areas.code`), np. `hr`. */
  areaCode: string;
  /** Klucz i18n nazwy obszaru na planszy „nieaktywny" (obszaru nie ma w odpowiedzi, gdy brak modułów). */
  areaLabelKey: string;
  children: ReactNode;
}

export function ModuleAreaGuard({ areaCode, areaLabelKey, children }: ModuleAreaGuardProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useInstalledModulesMenuQuery();

  if (isLoading) return <ModuleGuardLoading />;
  if (isError || !data) return <ModuleGuardError />;

  if (data.some((area) => area.code === areaCode)) return <>{children}</>;
  return <ModuleInactiveNotice moduleName={t(areaLabelKey)} />;
}
